
// frontend/src/pages/SportsPage.js
// REDESIGNED: XtreamGate Premium Sports Hub
import React, { useEffect } from 'react';
import { MainNavigation } from '@/components/MainNavigation';
import { Footer } from '@/components/Footer';
import { GridBackground } from '@/components/GridBackground';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ScoreboardGrid } from '@/components/Sports/ScoreboardGrid';
import { NewsGrid } from '@/components/Sports/NewsGrid';
import { Link } from 'react-router-dom';
import { Trophy, ChevronRight, Tv, Zap, Globe, Flag, Activity } from 'lucide-react';

const LeagueCard = ({ name, to, icon: Icon, gradient }) => (
    <Link to={to} className="group">
        <motion.div
            whileHover={{ scale: 1.03, y: -4 }}
            whileTap={{ scale: 0.98 }}
            className={`
                relative overflow-hidden rounded-2xl p-6 h-full
                border border-white/10 hover:border-primary/50
                transition-all duration-300
                bg-gradient-to-br ${gradient}
            `}
            style={{
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(12px)',
            }}
        >
            {/* Glow Effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-primary/10 via-transparent to-transparent" />

            <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-white group-hover:text-primary transition-colors">{name}</h3>
                        <p className="text-xs text-muted-foreground">Live Scores & News</p>
                    </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
            </div>
        </motion.div>
    </Link>
);

export const SportsPage = () => {
    useEffect(() => {

        window.scrollTo(0, 0);
    }, []);

    const LEAGUES = [
        { name: 'Premier League', to: '/sports/soccer/premier-league', icon: Globe, gradient: 'from-purple-600/20 to-indigo-600/10' },
        { name: 'La Liga', to: '/sports/soccer/la-liga', icon: Activity, gradient: 'from-orange-600/20 to-red-600/10' },
        { name: 'Bundesliga', to: '/sports/soccer/bundesliga', icon: Trophy, gradient: 'from-red-600/20 to-yellow-600/10' },
        { name: 'Serie A', to: '/sports/soccer/serie-a', icon: Globe, gradient: 'from-blue-400/20 to-cyan-500/10' },
        { name: 'Champions League', to: '/sports/soccer/champions-league', icon: Trophy, gradient: 'from-blue-900/20 to-indigo-900/10' },
        { name: 'NBA', to: '/sports/basketball/nba', icon: Zap, gradient: 'from-orange-600/20 to-red-600/10' },
        { name: 'NFL', to: '/sports/football/nfl', icon: Tv, gradient: 'from-blue-700/20 to-red-700/10' },
        { name: 'Formula 1', to: '/sports/racing/f1', icon: Flag, gradient: 'from-red-600/20 to-zinc-900/10' },
    ];

    return (
        <div className="min-h-screen relative overflow-hidden">
            <Helmet>
                {/* Primary Meta Tags */}
                <title>Live Sports Scores & Streaming Guide | XtreamGate Sports Hub</title>
                <meta name="title" content="Live Sports Scores & Streaming Guide | XtreamGate Sports Hub" />
                <meta name="description" content="Real-time live scores for Premier League, NBA, NFL, Champions League. Breaking sports news and the ultimate cord-cutting guide to stream sports in 4K without cable." />
                <meta name="keywords" content="live sports scores, sports streaming, watch sports without cable, IPTV sports, Premier League live, NBA streaming, NFL live stream, free sports streaming" />

                {/* Canonical URL */}
                <link rel="canonical" href="https://xtreamgate.com/sports" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://xtreamgate.com/sports" />
                <meta property="og:title" content="Live Sports Scores & Streaming Guide | XtreamGate" />
                <meta property="og:description" content="Real-time scores for Premier League, NBA, NFL. Stream sports in 4K without cable." />
                <meta property="og:image" content="https://xtreamgate.com/og-sports.jpg" />
                <meta property="og:site_name" content="XtreamGate" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Live Sports Scores & Streaming Guide | XtreamGate" />
                <meta name="twitter:description" content="Real-time scores for Premier League, NBA, NFL. Stream sports in 4K without cable." />
                <meta name="twitter:image" content="https://xtreamgate.com/og-sports.jpg" />

                {/* Additional SEO */}
                <meta name="robots" content="index, follow" />
                <meta name="author" content="XtreamGate" />

                {/* JSON-LD Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebPage",
                        "name": "Live Sports Hub",
                        "description": "Real-time live scores and streaming guides for Premier League, NBA, NFL, and Champions League.",
                        "url": "https://xtreamgate.com/sports",
                        "publisher": {
                            "@type": "Organization",
                            "name": "XtreamGate",
                            "url": "https://xtreamgate.com"
                        },
                        "mainEntity": {
                            "@type": "ItemList",
                            "name": "Sports Leagues",
                            "itemListElement": [
                                { "@type": "ListItem", "position": 1, "name": "Premier League", "url": "https://xtreamgate.com/sports/soccer/premier-league" },
                                { "@type": "ListItem", "position": 2, "name": "NBA", "url": "https://xtreamgate.com/sports/basketball/nba" },
                                { "@type": "ListItem", "position": 3, "name": "NFL", "url": "https://xtreamgate.com/sports/football/nfl" },
                                { "@type": "ListItem", "position": 4, "name": "Champions League", "url": "https://xtreamgate.com/sports/soccer/champions-league" }
                            ]
                        }
                    })}
                </script>
            </Helmet>

            <MainNavigation />
            <GridBackground />

            {/* Breadcrumbs */}
            <nav className="relative z-10 py-4 px-4">
                <div className="max-w-7xl mx-auto">
                    <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                        <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                        <li>/</li>
                        <li><span className="text-primary font-medium">Sports Hub</span></li>
                    </ol>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative z-10 pt-32 pb-16 px-4">
                <div className="max-w-7xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6 backdrop-blur-md shadow-lg">
                            <Trophy className="w-4 h-4 text-primary" />
                            <span className="text-sm font-bold text-primary uppercase tracking-wider">Real-Time Scores</span>
                        </div>

                        {/* Title */}
                        <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
                            Live <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400">Sports Hub</span>
                        </h1>

                        {/* Description */}
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                            Real-time scores, breaking news, and the ultimate guide to watching your favorite leagues without cable.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 pb-24">

                {/* League Navigation */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mb-16"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-1 h-8 bg-gradient-to-b from-primary to-blue-500 rounded-full" />
                        <h2 className="text-2xl font-bold text-white">Popular Leagues</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {LEAGUES.map((league, index) => (
                            <motion.div
                                key={league.name}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 * index }}
                            >
                                <LeagueCard {...league} />
                            </motion.div>
                        ))}
                    </div>
                </motion.section>

                {/* Live Scoreboard */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mb-16"
                >
                    <div
                        className="rounded-3xl p-6 md:p-8 border border-white/10"
                        style={{
                            background: 'rgba(255, 255, 255, 0.02)',
                            backdropFilter: 'blur(20px)',
                        }}
                    >
                        <ScoreboardGrid sport="soccer" league="eng.1" />
                    </div>
                </motion.section>

                {/* CTA Banner - Stream Sports Live */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mb-16"
                >
                    <div className="rounded-2xl p-6 md:p-8 bg-gradient-to-r from-primary/20 via-primary/10 to-blue-600/20 border border-primary/30 relative overflow-hidden">
                        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" />

                        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="text-center md:text-left">
                                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 rounded-full text-primary text-xs font-bold uppercase mb-3">
                                    <Tv className="w-3 h-3" />
                                    Live Now
                                </div>
                                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                                    Never Miss a Game Again
                                </h3>
                                <p className="text-muted-foreground max-w-md">
                                    Stream every Premier League, NBA, NFL, and F1 event in 4K. No cable required.
                                </p>
                            </div>
                            <a
                                href="https://xtreamcube.shop/freetrialiptv/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-bold rounded-xl shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:scale-105 transition-all"
                            >
                                <Zap className="w-5 h-5 fill-current" />
                                Start Free Trial
                            </a>
                        </div>
                    </div>
                </motion.section>

                {/* News Section */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <NewsGrid category="all" />
                </motion.section>

                {/* Related Sports Guides - SEO Cross-Linking */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="mt-16"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Trophy className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white">Sports Streaming Guides</h2>
                            <p className="text-sm text-muted-foreground">Expert guides for cord-cutting sports fans</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Guide Card 1 */}
                        <Link
                            to="/blog/best-iptv-for-sports-2026"
                            className="group p-5 rounded-2xl border border-white/10 hover:border-primary/50 transition-all bg-white/5 hover:bg-white/10"
                        >
                            <div className="flex items-center gap-2 text-xs text-primary font-bold mb-2">
                                <Zap className="w-3 h-3" />
                                FEATURED GUIDE
                            </div>
                            <h3 className="text-lg font-bold text-white group-hover:text-primary transition-colors mb-2">
                                Best IPTV for Sports 2026
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Stream NBA, NFL, Premier League & UFC in 4K without cable. Complete setup guide.
                            </p>
                        </Link>

                        {/* Guide Card 2 */}
                        <Link
                            to="/blog/how-to-watch-premier-league-usa-2025"
                            className="group p-5 rounded-2xl border border-white/10 hover:border-primary/50 transition-all bg-white/5 hover:bg-white/10"
                        >
                            <div className="flex items-center gap-2 text-xs text-muted-foreground font-medium mb-2">
                                <Globe className="w-3 h-3" />
                                PREMIER LEAGUE
                            </div>
                            <h3 className="text-lg font-bold text-white group-hover:text-primary transition-colors mb-2">
                                Watch Premier League in USA
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Every match, every team. No blackouts. Stream Arsenal, Man City, Liverpool & more.
                            </p>
                        </Link>

                        {/* Guide Card 3 */}
                        <Link
                            to="/blog/how-to-watch-nba-live-stream-2025"
                            className="group p-5 rounded-2xl border border-white/10 hover:border-primary/50 transition-all bg-white/5 hover:bg-white/10"
                        >
                            <div className="flex items-center gap-2 text-xs text-muted-foreground font-medium mb-2">
                                <Activity className="w-3 h-3" />
                                NBA BASKETBALL
                            </div>
                            <h3 className="text-lg font-bold text-white group-hover:text-primary transition-colors mb-2">
                                Stream NBA Games Live
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Watch every game including playoffs and Finals. Compatible with all devices.
                            </p>
                        </Link>
                    </div>

                    {/* More Guides Link */}
                    <div className="mt-6 text-center">
                        <Link
                            to="/blog"
                            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-medium transition-colors"
                        >
                            Browse All Streaming Guides
                            <ChevronRight className="w-4 h-4" />
                        </Link>
                    </div>
                </motion.section>

            </div>

            <Footer />
        </div>
    );
};
